import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import Tag from "ui-components/molecules/tag/tag";
import TerminalLocation from "ui-components/terminal-location/terminalLocation";
import { TagColor } from "app/data/common/tagColor";
import { PlanningFreightItem } from "app/data/linehaul-outbound-planning/models";
import { formatIsoToLocal } from "../utils/relativeTimeUtils";

const columnHelper = createColumnHelper<PlanningFreightItem>();

const formatFreightStatusLabel = (value: string | null | undefined): string => {
  if (value == null || String(value).trim() === "") return "—";
  const normalized = String(value).replace(/_/g, " ").toLowerCase();
  return normalized.replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatNumber = (value: number | undefined) =>
  value != null ? value.toLocaleString("en", { useGrouping: true }) : "—";

const formatDimension = (value: number | undefined) =>
  value != null ? value.toLocaleString("en", { useGrouping: true, maximumFractionDigits: 2 }) : "—";

export const OUTBOUND_PLANNING_ITEM_COLUMN_OPTIONS = [
  { id: "itemSequenceNumber", label: "Item Seq" },
  { id: "itemDescription", label: "Description" },
  { id: "pieces", label: "Pieces" },
  { id: "weight", label: "Weight" },
  { id: "length", label: "Length" },
  { id: "width", label: "Width" },
  { id: "height", label: "Height" },
  { id: "itemStatus", label: "Status" },
  { id: "itemLocation", label: "Location" },
  { id: "lastChanged", label: "Last Changed" },
  { id: "lastChangeUser", label: "Last Changed By" },
  { id: "updatedAt", label: "Updated At" },
  { id: "rollNumber", label: "Roll Number" },
  { id: "serialNumber", label: "Serial Number" },
  { id: "itemType", label: "Type" },
  { id: "itemIsRoll", label: "Is Roll" },
  { id: "sku", label: "SKU" },
  { id: "itemPo", label: "Item PO" },
  { id: "partNumber", label: "Part Number" },
  { id: "itemCommodityCode", label: "Commodity Code" },
  { id: "itemCommodityCodeDescription", label: "Commodity Description" },
  { id: "style", label: "Style" },
  { id: "color", label: "Color" },
  { id: "dyeLot", label: "Dye Lot" },
  { id: "sideMark", label: "Side Mark" },
  { id: "addedAt", label: "Added At" },
];

export const OUTBOUND_PLANNING_ITEM_COLUMNS_HIDDEN_BY_DEFAULT = [
  "length",
  "width",
  "height",
  "lastChangeUser",
  "updatedAt",
  "itemType",
  "itemIsRoll",
  "sku",
  "itemPo",
  "partNumber",
  "itemCommodityCode",
  "itemCommodityCodeDescription",
  "style",
  "color",
  "dyeLot",
  "sideMark",
  "addedAt",
];

export function getOutboundPlanningItemColumns() {
  return [
    columnHelper.accessor("itemSequenceNumber", {
      id: "itemSequenceNumber",
      header: "Item Seq",
      size: 80,
    }),
    columnHelper.accessor("itemDescription", {
      id: "itemDescription",
      header: "Description",
      size: 200,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("pieces", {
      id: "pieces",
      header: "Pieces",
      size: 80,
      cell: (cellProps) => formatNumber(cellProps.getValue()),
    }),
    columnHelper.accessor("weight", {
      id: "weight",
      header: "Weight",
      size: 80,
      cell: (cellProps) => formatNumber(cellProps.getValue()),
    }),
    columnHelper.accessor("length", {
      id: "length",
      header: "Length",
      size: 80,
      cell: (cellProps) => formatDimension(cellProps.getValue()),
    }),
    columnHelper.accessor("width", {
      id: "width",
      header: "Width",
      size: 80,
      cell: (cellProps) => formatDimension(cellProps.getValue()),
    }),
    columnHelper.accessor("height", {
      id: "height",
      header: "Height",
      size: 80,
      cell: (cellProps) => formatDimension(cellProps.getValue()),
    }),
    columnHelper.accessor("itemStatus", {
      id: "itemStatus",
      header: "Status",
      size: 120,
      cell: (cellProps) => {
        const value = cellProps.getValue();
        const color =
          value === "AT_YARD"
            ? TagColor.YELLOW
            : value === "STAGED" || value === "RACK"
              ? TagColor.GREEN
              : value === "INBOUND"
                ? TagColor.BLUE
                : TagColor.GREY;
        return (
          <Tag mods={{ color }}>{formatFreightStatusLabel(cellProps.getValue())}</Tag>
        );
      },
    }),
    columnHelper.accessor("itemLocation", {
      id: "itemLocation",
      header: "Location",
      size: 100,
      cell: (cellProps) => <TerminalLocation locationCode={cellProps.getValue()} />,
    }),
    columnHelper.display({
      id: "lastChanged",
      header: "Last Changed",
      size: 180,
      cell: ({ row }) => {
        const user = row.original.lastChangeUser;
        const updated = formatIsoToLocal(row.original.updatedAt);
        if (!user && !updated) return "—";
        return (
          <div>
            {user && <div>{user}</div>}
            {updated && <div className="outbound-planning-item-last-changed__time">{updated}</div>}
          </div>
        );
      },
    }),
    columnHelper.accessor("lastChangeUser", {
      id: "lastChangeUser",
      header: "Last Changed By",
      size: 140,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("updatedAt", {
      id: "updatedAt",
      header: "Updated At",
      size: 150,
      cell: (cellProps) => formatIsoToLocal(cellProps.getValue()) || "—",
    }),
    columnHelper.accessor("rollNumber", {
      id: "rollNumber",
      header: "Roll Number",
      size: 110,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("serialNumber", {
      id: "serialNumber",
      header: "Serial Number",
      size: 120,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("itemType", {
      id: "itemType",
      header: "Type",
      size: 80,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("itemIsRoll", {
      id: "itemIsRoll",
      header: "Is Roll",
      size: 80,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("sku", {
      id: "sku",
      header: "SKU",
      size: 90,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("itemPo", {
      id: "itemPo",
      header: "Item PO",
      size: 100,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("partNumber", {
      id: "partNumber",
      header: "Part Number",
      size: 110,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("itemCommodityCode", {
      id: "itemCommodityCode",
      header: "Commodity Code",
      size: 120,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("itemCommodityCodeDescription", {
      id: "itemCommodityCodeDescription",
      header: "Commodity Description",
      size: 180,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("style", {
      id: "style",
      header: "Style",
      size: 90,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("color", {
      id: "color",
      header: "Color",
      size: 90,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("dyeLot", {
      id: "dyeLot",
      header: "Dye Lot",
      size: 90,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("sideMark", {
      id: "sideMark",
      header: "Side Mark",
      size: 100,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("addedAt", {
      id: "addedAt",
      header: "Added At",
      size: 150,
      cell: (cellProps) => formatIsoToLocal(cellProps.getValue()) || "—",
    }),
  ];
}
